import { Adapter } from '@fmnts/core';
import { InlineTranslateable } from '@fmnts/i18n';
import { IFieldCondition } from '../api-model/condition';
import { IApiFormBaseField, IApiFormField } from '../api-model/forms.api';
import {
  ActionField,
  FieldWidget,
  FormField,
  ISelectInputField,
  ISelectInputItem,
  ITextInputField,
  IValidatorConfig,
  InputField,
  SelectInputField,
  TextInputField,
  TextboxField,
  WidgetType,
} from './form-field';
import { IntervalBase } from './payment-interval';

// ==================================================================
// Interfaces for Campaign Field Types
// ==================================================================
interface ICampaignSelect {
  items: CampaignSelectFieldItem[];
  required?: boolean | undefined;
  validators: IValidatorConfig[];
  hasTypeAhead?: boolean;
  scope: string;
  defaultValue?: string | CampaignSelectFieldItem[];
  itemConditions?: { [key: string]: IFieldCondition } | undefined;
}

interface ICampaignSummary {
  required?: boolean | undefined;
  scope: string | undefined;
  validators: IValidatorConfig[];
  defaultValue: string;
}
export interface ICampaignSponsorshipItem extends CampaignSelectFieldItem {
  /**
   * Interval that defines how any `amount` of this sponsorship
   * item has to be interpreted.
   *
   * @example
   * // minimum 12 per year (= 1 per month)
   * { calculation_base: 'yearly', min_amount: 12, ...}
   */
  calculation_base: IntervalBase;
  /** Translateable description text */
  description: InlineTranslateable;
  /** URL to image for the sponsorship */
  image_url: string;
  /** Optional minimum amount for the sponsorship */
  min_amount?: number;
  /**
   * `true` if the sponsorships min amount should be set as the amount
   * when the sponsorship is selected
   */
  set_min_amount?: boolean;
  /** Optional custom styling */
  style?: string;
}
interface ICampaignSponsorship extends ICampaignSelect {
  items: ICampaignSponsorshipItem[];
}

interface ICampaignAddressSearch extends ITextInputField {
  /**
   * Maps field keys to address search components
   */
  mapping: { [fieldKey: string]: string[] };
  /**
   * Provider for address-search
   * Can be either `loqate` or `google`
   */
  provider: string;
}
/**
 * Represents an item for a select-like field
 */
export type CampaignSelectFieldItem = ISelectInputItem;

export class CampaignField<
  TWidgetType extends WidgetType = WidgetType,
  T extends FieldWidget = any,
> extends FormField<TWidgetType, T> {
  /**
   * for connecting to salesforce we need to transfer the
   * additional fields api name to raisenow
   */
  public apiFieldName: string | undefined;
  /**
   * Helper property for `apiFieldName`.
   * Only sends field that are marked as additional to salesforce
   */
  public isAdditionalField: boolean | undefined;

  /**
   * This description is currently used for single checkboxes for
   * widget key `checkbox`
   */
  public description: string | undefined;

  constructor(
    dto: Readonly<IApiFormBaseField> & Record<string, any>,
    fieldOptions: T,
    fieldType?: TWidgetType,
  ) {
    super(dto, fieldOptions, fieldType);
    this.isAdditionalField = dto['isAdditionalField'];
    this.apiFieldName = dto['apiFieldName'];
    this.description = dto['description'];
  }
}

/**
 * Utility Types for CampaignFields
 */
export type CampaignSummaryField = CampaignField<'input', CampaignSummary>;
export type CampaignMultiSelectSummaryField = CampaignField<
  'input',
  CampaignMultiSelectTextbox
>;
export type CampaignSingleSelectSummaryField = CampaignField<
  'input',
  CampaignSingleSelectTextbox
>;
export type CampaignSponsorshipField = CampaignField<
  'input',
  CampaignSponsorship
>;
export type CampaignSingleSelectInputField = CampaignField<
  'input',
  CampaignSingleSelectInput
>;
export type CampaignMultiSelectInputField = CampaignField<
  'input',
  CampaignMultiSelectInput
>;
export type CampaignAddressSearchField = CampaignField<
  'input',
  CampaignAddressSearch
>;

// ==================================================================
// Campaign Field Types
// ==================================================================

export class CampaignSponsorship
  extends SelectInputField
  implements ISelectInputField
{
  override items: ICampaignSponsorshipItem[];

  constructor(obj: ICampaignSponsorship) {
    super(obj);
    this.items = obj.items;
  }
}

export class CampaignSingleSelectInput extends SelectInputField {
  override defaultValue?: CampaignSelectFieldItem;
}

export class CampaignSingleSelectTextbox extends SelectInputField {
  override defaultValue?: CampaignSelectFieldItem | string;
}

export class CampaignMultiSelectInput extends SelectInputField {
  override defaultValue?: CampaignSelectFieldItem;
}

export class CampaignMultiSelectTextbox extends SelectInputField {
  override defaultValue?: CampaignSelectFieldItem[] | string;
}

export class CampaignSummary extends InputField {
  /**
   * The static value for this summary field.
   * May only be available, if `value` is undefined.
   * In case both are defined, `staticValue` should have precedence before `value`.
   */
  override defaultValue: string;

  constructor(obj: ICampaignSummary) {
    super(obj);
    this.defaultValue = obj.defaultValue;
  }
}

export class CampaignAddressSearch extends TextInputField {
  /**
   * Maps field keys to address search components
   */
  public mapping: { [fieldKey: string]: string[] };
  /**
   * Provider for address-search
   * Can be either `loqate` or `google`
   */
  public provider: string;

  constructor(obj: ICampaignAddressSearch) {
    super(obj);
    this.mapping = obj.mapping;
    this.provider = obj.provider;
  }
}

export class CampaignFieldAdapter implements Adapter<FormField> {
  adapt(item: Partial<IApiFormField> & Record<string, any>): CampaignField {
    const baseField = {
      key: item['key'] || '',
      label: item['label'] || '',
      condition: item['condition'],
      style: item['style'],
      widget: item['widget'],
      apiFieldName: item['api_field_name'],
      isAdditionalField: item['is_additional_field'],
      description: item['description'],
      ...item,
    };

    switch (item['widget']) {
      case 'amountChoice':
      case 'checkbox':
      case 'multiIntervalAmountChoice':
      case 'radioToggle':
      case 'radiosWithContent':
      case 'select':
      case 'sponsorship':
      case 'starRating':
        return new CampaignField(
          baseField,
          new CampaignSingleSelectInput({
            items: item['items'] || [],
            itemConditions: item['item_conditions'],
            required: item['required'],
            scope: item['scope'],
            validators: item['validators'] || [],
            defaultValue: item['value'],
            hasTypeAhead: item['has_type_ahead'],
          }),
          'input',
        );
      case 'checkboxesWithContent':
      case 'checkboxes':
        return new CampaignField(
          baseField,
          new CampaignMultiSelectInput({
            items: item['items'] || [],
            itemConditions: item['item_conditions'],
            required: item['required'],
            scope: item['scope'],
            validators: item['validators'] || [],
            defaultValue: item['value'],
            hasTypeAhead: item['has_type_ahead'],
          }),
          'input',
        );
      case 'checkboxgroup_summary':
      case 'compliments_summary':
        return new CampaignField(
          baseField,
          new CampaignMultiSelectTextbox({
            items: item['items'] || [],
            itemConditions: item['item_conditions'],
            required: item['required'],
            scope: item['scope'],
            validators: item['validators'] || [],
            defaultValue: item['value'],
            hasTypeAhead: item['has_type_ahead'],
          }),
          'input',
        );
      case 'radiogroup_summary':
        return new CampaignField(
          baseField,
          new CampaignSingleSelectTextbox({
            items: item['items'] || [],
            itemConditions: item['item_conditions'],
            required: item['required'],
            scope: item['scope'],
            validators: item['validators'] || [],
            defaultValue: item['value'],
          }),
          'input',
        );
      case 'rating_summary':
      case 'amount_summary':
      case 'date_summary':
      case 'payment_summary':
      case 'text_summary':
        return new CampaignField(
          baseField,
          new CampaignSummary({
            defaultValue: item['value'],
            scope: item['scope'],
            validators: item['validators'] || [],
          }),
          'input',
        );
      case 'text':
      case 'phone_number':
      case 'customDateField':
        return new CampaignField(
          baseField,
          new TextInputField({
            autocapitalize: item['autocapitalize'],
            keyboardType: item['keyboard_type'],
            format: item['format'],
            scope: item['scope'],
            required: item['required'],
            validators: item['validators'] || [],
            defaultValue: item['value'],
          }),
          'input',
        );
      case 'textbox':
        return new CampaignField(
          baseField,
          new TextboxField({
            text: item['text'] || '',
            assets: item['assets'] || [],
            image: item['image'] || '',
          }),
          'textbox',
        );
      case 'googleAddressSearch':
        return new CampaignField(
          baseField,
          new CampaignAddressSearch({
            mapping: item['mapping'],
            provider: item['provider'],
            defaultValue: item['value'],
            scope: item['scope'],
            validators: item['validators'] || [],
            required: item['required'],
            keyboardType: item['keyboard_type'],
            autocapitalize: item['autocapitalize'],
          }),
          'input',
        );
      case 'button':
        return new CampaignField(
          baseField,
          new ActionField({
            action: item['action'] || '',
            actionConfig: item['action_config'],
          }),
          'action',
        );

      case 'signaturePad':
      case 'taxIdentificationNumber':
        return new CampaignField(
          baseField,
          new InputField({
            defaultValue: item['value'],
            scope: item['scope'],
            required: item['required'],
            validators: item['validators'] ?? [],
          }),
          'input',
        );
      default:
        return new CampaignField(baseField, undefined);
    }
  }
}
